import { FieldValue, QueryRule } from '../../../../../types/QueryBuilder';
import { Translate } from '../ValueEditors/valueUtils';

export type ChannelKey = 'email' | 'sms' | 'courrier';

export const CHANNEL_KEYS: ChannelKey[] = ['email', 'sms', 'courrier'];

interface ChannelPriority {
    channel: ChannelKey;
    excluded: ChannelKey[];
}

const CHANNEL_LABEL_RE = /^(email|sms|courrier)\s+à tous\s+sauf\s+(.+)$/i;
const LABEL_KEY_PREFIX = 'pages.clients.queryBuilder.simple.channelPriority';

const toChannelKey = (raw: string): ChannelKey | undefined => {
    const key = raw.trim().toLowerCase();
    return CHANNEL_KEYS.find(channel => channel === key);
};

export const exclusionKey = (excluded: ChannelKey[]): string => [...excluded].sort().join(',');

export const parseExclusionKey = (key: string): ChannelKey[] => key.split(',') as ChannelKey[];

const comboKey = ({ channel, excluded }: ChannelPriority): string => `${channel}|${exclusionKey(excluded)}`;

function parseChannelPriorityLabel(label: string): ChannelPriority | undefined {
    const match = label.trim().match(CHANNEL_LABEL_RE);
    const channel = match && toChannelKey(match[1]);
    if (!match || !channel) return undefined;
    const excluded = match[2].split(/\s+et\s+|,/i).map(toChannelKey).filter((key): key is ChannelKey => key !== undefined);
    return excluded.length > 0 ? { channel, excluded } : undefined;
}

export function parseChannelPriorityValues(values: FieldValue[] | undefined) {
    const nameByCombo = new Map<string, string>();
    const priorityByName = new Map<string, ChannelPriority>();
    (values ?? []).forEach(({ name, label }) => {
        const priority = parseChannelPriorityLabel(label);
        if (!priority) return;
        nameByCombo.set(comboKey(priority), name);
        priorityByName.set(name, priority);
    });

    const nameOf = (priority: ChannelPriority) => nameByCombo.get(comboKey(priority));

    return {
        nameOf,
        priorityOf: (value: QueryRule['value']): ChannelPriority | undefined => {
            const name = Array.isArray(value) ? value[0] : value;
            return name === undefined || name === null || name === '' ? undefined : priorityByName.get(String(name));
        },
        exclusionOptions: (channel: ChannelKey): ChannelKey[][] => {
            const others = CHANNEL_KEYS.filter(key => key !== channel);
            return [[others[0]], [others[1]], others].filter(excluded => nameOf({ channel, excluded }) !== undefined);
        },
    };
}

export function channelPriorityLabels(t: Translate) {
    const channelLabel = (key: ChannelKey) => t(`${LABEL_KEY_PREFIX}.${key}`);
    return {
        channelLabel,
        exclusionLabel: (keys: ChannelKey[]) => keys.map(channelLabel).join(` ${t(`${LABEL_KEY_PREFIX}.and`)} `),
    };
}
